import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Alert, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, OutlinedInput, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, Home, Group, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyForm = { code: '', description: '', permissionSets: [] };

const UserGroupList = () => {
  const navigate = useNavigate();
  const [groups,    setGroups]    = useState([]);
  const [pSets,     setPSets]     = useState([]);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [dlgOpen,   setDlgOpen]   = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dlgOpen, value: form, onClose: () => setDlgOpen(false) });
  const [saving,    setSaving]    = useState(false);
  const [confirmDlg,setConfirmDlg]= useState(null);

  const load = useCallback(async () => {
    try {
      const [gRes, psRes] = await Promise.all([
        axios.get(`${API}/api/user-groups`,     { headers: hdrs() }),
        axios.get(`${API}/api/permission-sets`, { headers: hdrs() }),
      ]);
      setGroups(gRes.data);
      setPSets(psRes.data);
    } catch { setError('Failed to load data'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setError(''); setDlgOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({ code: g.code, description: g.description || '', permissionSets: g.permissionSets || [] });
    setError(''); setDlgOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Code is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/user-groups/${editing.code}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/user-groups`, form, { headers: hdrs() });
      }
      setDlgOpen(false); load();
      setSuccess(editing ? 'User Group updated' : 'User Group created');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const g = confirmDlg; setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/user-groups/${g.code}`, { headers: hdrs() });
      load(); setSuccess('Deleted'); setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Group color="primary" />
              <Typography variant="h5" color="primary">User Groups</Typography>
              <Chip label={groups.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Setups">
                <IconButton onClick={() => navigate('/setups')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Permission Sets</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>No user groups yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : groups.map(g => (
                <TableRow key={g._id} hover>
                  <TableCell><strong>{g.code}</strong></TableCell>
                  <TableCell>{g.description}</TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {(g.permissionSets || []).map(ps => (
                        <Chip key={ps} label={ps} size="small" variant="outlined" color="info" />
                      ))}
                      {(!g.permissionSets || g.permissionSets.length === 0) && (
                        <Typography variant="caption" color="text.secondary">None</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(g)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDlg(g)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirm Delete */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete user group <strong>{confirmDlg?.code}</strong>? Users in this group will lose its permissions.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      {discardDialog}
      <Dialog open={dlgOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.code}` : 'New User Group'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Code *" value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            fullWidth size="small" disabled={!!editing} />
          <TextField label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            fullWidth size="small" />
          <FormControl fullWidth size="small">
            <InputLabel>Permission Sets</InputLabel>
            <Select multiple value={form.permissionSets}
              onChange={e => setForm(f => ({ ...f, permissionSets: e.target.value }))}
              input={<OutlinedInput label="Permission Sets" />}
              renderValue={selected => (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {selected.map(v => <Chip key={v} label={v} size="small" />)}
                </Box>
              )}>
              {pSets.map(ps => (
                <MenuItem key={ps.code} value={ps.code}>
                  <strong>{ps.code}</strong>&nbsp;— {ps.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserGroupList;
